import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import DesktopMenu from './components/desktop-menu'
import MobileMenu from './components/mobile-menu'
import { useRouter } from 'next/router'
import Link from 'next/link'

import Image from 'next/image'

import { useTranslation } from 'utils/use-translation'

import Logo from './icons/logo.png'
import CryptoLoanIcon from './icons/crypto-loans.svg'
import CryptoWalletIcon from './icons/crypto-wallet.svg'
import ExchangeCryptonIcon from './icons/exchange-crypto.svg'
import EarnIterestIcon from './icons/earn-interest.svg'
import ApiIntegrationIcon from './icons/api-integration.svg'
import ReferralProgramIcon from './icons/referral-program.svg'
import LogoWhiteIcon from './icons/logo-white.png'
import CryptoLoansWhiteIcon from './icons/crypto-loans-white.svg'
import CryptoWalletWhiteIcon from './icons/crypto-wallet-white.svg'
import EarnInterestWhiteIcon from './icons/earn-interest-white.svg'

import cn from 'classnames'
import useWindowWidth from 'hooks/use-window-width'

export interface DropdownMenuItem {
    icon: string
    title: string
    description: string
    link: string
}

export interface MenuItem {
    item: string
    link: string
    items: DropdownMenuItem[]
}

export const MenuToggleButton = ({ isOpen, onClick, className = '', type = 'primary' }) => {
    return (
        <button
            className={cn(styles['menu-toggle-button'], {
                [styles['menu-toggle-button__active']]: isOpen,
                [styles['secondary']]: type !== 'primary'
            }, className)}
            onClick={onClick}
        >
            <div className={styles["menu-toggle-button__line"]}></div>
            <div className={styles["menu-toggle-button__line"]}></div>
            <div className={styles["menu-toggle-button__line"]}></div>
        </button>
    )
}

const Header = ({
    type = 'primary',
    isMainPage,
}: {
    type?: 'primary' | 'secondary' | 'tretiary',
    isMainPage?: boolean
}) => {
    const { t } = useTranslation()
    const router = useRouter()
    const width = useWindowWidth()

    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isLocalePopupOpen, setIsLocalePopupOpen] = useState(false)
    const [isHeaderVisible, setIsHeaderVisible] = useState(true)
    const [lastScrollY, setLastScrollY] = useState(0)

    const scrollYRef = useRef(0)

    const toggleMenu = () => {
        setIsLocalePopupOpen(false)
        setIsMenuOpen(prev => !prev)
    }

    const onClose = () => {
        setIsLocalePopupOpen(false)
        setIsMenuOpen(false)
    }

    useEffect(() => {
        if (isMenuOpen) {
            scrollYRef.current = window.scrollY;

            document.body.style.overflow = 'hidden'
            document.body.style.position = 'fixed'
            document.body.style.top = `-${scrollYRef.current}px`
            document.body.style.width = '100%'
        } else {
            document.body.style.overflow = ''
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            window.scrollTo(0, scrollYRef.current)
            setIsHeaderVisible(true)
        }
    }, [isMenuOpen])

    useEffect(() => {
        if (isMenuOpen) return undefined

        const handleScroll = () => {
            const currentScrollY = window.scrollY

            if (currentScrollY > lastScrollY && currentScrollY > 0) {
                setIsHeaderVisible(false)
            } else if (currentScrollY < lastScrollY) {
                setIsHeaderVisible(true)
            }

            setLastScrollY(currentScrollY)
        }

        let ticking = false

        const throttledHandleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll()
                    ticking = false
                });
                ticking = true
            }
        }

        window.addEventListener('scroll', throttledHandleScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', throttledHandleScroll)
        }
    }, [lastScrollY, isMenuOpen])

    const menuItems: MenuItem[] = [
        {
            item: t('for_personal'),
            link: '',
            items: [
                { icon: type !== 'primary' ? CryptoLoansWhiteIcon : CryptoLoanIcon, title: t('crypto_loans'), description: t('crypto_loans_menu'), link: '/' },
                { icon: type !== 'primary' ? CryptoWalletWhiteIcon : CryptoWalletIcon, title: t('crypto_wallet'), description: t('crypto_wallet_menu'), link: '/wallet' },
                { icon: type !== 'primary' ? EarnInterestWhiteIcon : EarnIterestIcon, title: t('earn_interest_title'), description: t('earn_interest_menu'), link: '/earn' },
                { icon: ExchangeCryptonIcon, title: t('exchange_crypto'), description: t('crypto_exchange_menu'), link: '/exchange' }
            ]
        }, {
            item: t('for_business'),
            link: '',
            items: [
                { icon: ApiIntegrationIcon, title: t('api_integration'), description: t('api_integration_menu'), link: '/partners' },
                { icon: ReferralProgramIcon, title: t('referral_program'), description: t('referral_program_menu'), link: '/referral' },
            ]
        },
        {
            item: t('vip_program'),
            link: '/private-clients',
            items: []
        },
        {
            item: t('about_us'),
            link: '/about',
            items: []
        },
        {
            item: t('blog'),
            link: '/blog',
            items: []
        }
    ]

    return (
        <>
            <header className={cn(styles.header, isHeaderVisible ? styles['header-visible'] : styles['header-hidden'], {
                [styles['header-scrolled']]: lastScrollY > 0,
                [styles['secondary']]: type !== 'primary'
            })}>
                <div className={styles.container}>
                    <div className={styles['logo-wrapper']}>
                        <div className={styles.logo}>
                            <Link prefetch={false} href={router.pathname.includes('earn') ? '/earn' : '/'}>
                                <Image
                                    src={type === 'primary' && !(isMainPage && width <= 1206 && lastScrollY < 10)
                                        ? Logo
                                        : LogoWhiteIcon
                                    }
                                    width={width > 1206 ? 161 : 121}
                                    height={width > 1206 ? 32 : 24}
                                />
                            </Link>
                        </div>
                    </div>

                    <DesktopMenu
                        type={type}
                        items={menuItems}
                        isLocalePopupOpen={isLocalePopupOpen}
                        setIsLocalePopupOpen={setIsLocalePopupOpen} />

                    <MenuToggleButton
                        isOpen={isMenuOpen}
                        onClick={toggleMenu}
                        type={(isMainPage && width <= 1206 && lastScrollY === 0) ? 'secondary' : type}
                    />
                </div>
            </header>

            <MobileMenu
                type={type}
                items={menuItems}
                isOpen={isMenuOpen}
                onClose={onClose}
                isLocalePopupOpen={isLocalePopupOpen}
                setIsLocalePopupOpen={setIsLocalePopupOpen}
            />
        </>
    )
}

export default Header